import { useEffect, useRef, useState } from "react";
import { format, isToday } from "date-fns";
import { nn } from "date-fns/locale";
import { toISODate, WEEKDAYS_SHORT } from "@/lib/format";
import { cn } from "@/lib/utils";
import { CalBar } from "./Bar";
import { dayColumns, isMultiDay, minToTime, onDay, sortByStart, toMin, weekDays, weekSegments, weekdayIndex } from "./engine";

const DAY_START = 7 * 60;
const DAY_END = 24 * 60;
const HOUR = 56; // px per hour
const SNAP = 30; // minutes

const minutesFromY = (y) => {
  const raw = DAY_START + (y / HOUR) * 60;
  return Math.min(DAY_END - SNAP, Math.max(DAY_START, Math.round(raw / SNAP) * SNAP));
};

const DayColumn = ({ day, iso, items, colours, onOpen, onCreate, onMove, children }) => {
  const [over, setOver] = useState(false);
  const ref = useRef(null);
  const blocks = dayColumns(items.filter((it) => !isMultiDay(it)), DAY_START, DAY_END);
  const yOf = (e) => e.clientY - ref.current.getBoundingClientRect().top;
  return (
    <div
      ref={ref}
      className={cn("cal-hour relative min-w-0 border-l border-border", isToday(day) && "cal-today", weekdayIndex(day) >= 5 && "cal-weekend", over && "cal-drop")}
      style={{ backgroundSize: `100% ${HOUR}px` }}
      onClick={(e) => { if (e.target !== e.currentTarget) return; const m = minutesFromY(yOf(e)); onCreate(iso, { start_time: minToTime(m), end_time: minToTime(Math.min(DAY_END, m + 120)) }); }}
      onDragOver={(e) => { if (e.dataTransfer?.types?.includes("text/plain")) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (!over) setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const id = e.dataTransfer.getData("text/plain"); if (id) onMove({ id, start_date: iso, start_min: minutesFromY(yOf(e)) }); }}
      data-testid={`cal-week-col-${iso}`}
    >
      {blocks.map((b) => (
        <div key={b.item.key} className="absolute px-[2px]" style={{ top: ((b.s - DAY_START) / 60) * HOUR + 1, height: Math.max(((b.e - b.s) / 60) * HOUR - 2, 22), left: `${(b.col / b.cols) * 100}%`, width: `${100 / b.cols}%` }}>
          <CalBar item={b.item} colours={colours} onOpen={onOpen} className="h-full items-start" style={{ alignItems: "flex-start", paddingTop: 5 }} />
        </div>
      ))}
      {children}
    </div>
  );
};

/** Live «now» line – only drawn on today's column, inside working hours. */
const NowLine = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60_000); return () => clearInterval(t); }, []);
  const min = now.getHours() * 60 + now.getMinutes();
  if (min < DAY_START || min > DAY_END) return null;
  return <div className="cal-now pointer-events-none absolute inset-x-0 z-10 h-[2px]" style={{ top: ((min - DAY_START) / 60) * HOUR }} data-testid="cal-now-line" />;
};

/**
 * Week view: 7 day columns on a 07–24 time axis. All-day and multi-day items live in a lane above the grid.
 * Click an empty slot to create; drag a block to another day/time (snaps to 30 min).
 */
export const WeekView = ({ anchor, items, colours, onOpen, onCreate, onMove }) => {
  const days = weekDays(anchor);
  const allDay = items.filter((it) => isMultiDay(it) || toMin(it.start_time) === null);
  const segs = weekSegments(allDay, days);
  const lanes = Math.max(1, ...segs.map((s) => s.lane + 1));
  const hours = Array.from({ length: (DAY_END - DAY_START) / 60 }, (_, i) => DAY_START / 60 + i);
  const move = ({ id, start_date, start_min }) => {
    const it = items.find((x) => x.kind === "entry" && x.id === id);
    const s = toMin(it?.start_time), e = toMin(it?.end_time);
    const dur = s !== null && e !== null ? e - s : 120;
    const payload = { id, start_date };
    if (start_min !== undefined && s !== null) { payload.start_time = minToTime(start_min); payload.end_time = minToTime(Math.min(DAY_END, start_min + dur)); }
    onMove(payload);
  };
  return (
    <div className="overflow-x-auto" data-testid="cal-week-view">
      <div className="min-w-[760px] overflow-hidden admin-card">
        <div className="grid grid-cols-[56px_repeat(7,minmax(0,1fr))] border-b border-border">
          <div className="px-2 py-2 t-mono-sm text-muted-foreground" aria-hidden="true" />
          {days.map((d, i) => {
            const today = isToday(d);
            return (
              <div key={toISODate(d)} className={cn("flex items-baseline gap-2 border-l border-border px-3 py-2.5", today && "cal-today")} data-testid={`cal-week-head-${toISODate(d)}`}>
                <span className={cn("text-[11px] font-medium uppercase tracking-[0.12em]", i >= 5 ? "text-muted-foreground/70" : "text-muted-foreground")}>{WEEKDAYS_SHORT[i]}</span>
                <span className={cn("flex h-7 min-w-7 items-center justify-center rounded-full px-1 font-display text-[17px] font-semibold tabular", today && "bg-sol text-ink")}>{format(d, "d")}</span>
                <span className="text-[12px] text-muted-foreground">{format(d, "MMM", { locale: nn })}</span>
              </div>
            );
          })}
        </div>
        {/* all-day lane */}
        <div className="relative grid grid-cols-[56px_repeat(7,minmax(0,1fr))] border-b border-border bg-secondary/30" style={{ minHeight: lanes * 29 + 10 }} data-testid="cal-week-allday">
          <div className="px-2 pt-2 text-[10px] font-medium uppercase leading-tight tracking-[0.1em] text-muted-foreground">Heile dagen</div>
          {days.map((d) => {
            const iso = toISODate(d);
            return <AllDayCell key={iso} iso={iso} onCreate={() => onCreate(iso, {})} onMove={(id) => onMove({ id, start_date: iso })} />;
          })}
          <div className="pointer-events-none absolute inset-y-0 left-[56px] right-0 grid grid-cols-7 content-start gap-y-[3px] pt-[5px]" style={{ gridAutoRows: "26px" }}>
            {segs.map((s) => (
              <div key={s.item.key} className="pointer-events-auto min-w-0 px-[3px]" style={{ gridColumn: `${s.start + 1} / ${s.end + 2}`, gridRow: s.lane + 1 }}>
                <CalBar item={s.item} colours={colours} before={s.before} after={s.after} onOpen={onOpen} showTime={false} />
              </div>
            ))}
          </div>
        </div>
        {/* time grid */}
        <div className="grid grid-cols-[56px_repeat(7,minmax(0,1fr))]" style={{ height: hours.length * HOUR }}>
          <div className="relative">
            {hours.map((h, i) => <span key={h} className="absolute right-2 -translate-y-1/2 text-[11px] text-muted-foreground tabular" style={{ top: i * HOUR }}>{i === 0 ? "" : `${String(h).padStart(2, "0")}:00`}</span>)}
          </div>
          {days.map((d) => {
            const iso = toISODate(d);
            const dayItems = sortByStart(items.filter((it) => onDay(it, iso) && !isMultiDay(it) && toMin(it.start_time) !== null));
            return (
              <DayColumn key={iso} day={d} iso={iso} items={dayItems} colours={colours} onOpen={onOpen} onCreate={onCreate} onMove={move}>
                {isToday(d) && <NowLine />}
              </DayColumn>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const AllDayCell = ({ iso, onCreate, onMove }) => {
  const [over, setOver] = useState(false);
  return (
    <div
      className={cn("min-w-0 cursor-cell border-l border-border transition-colors hover:bg-secondary/50", over && "cal-drop")}
      onClick={onCreate}
      onDragOver={(e) => { if (e.dataTransfer?.types?.includes("text/plain")) { e.preventDefault(); if (!over) setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const id = e.dataTransfer.getData("text/plain"); if (id) onMove(id); }}
      data-testid={`cal-week-allday-${iso}`}
    />
  );
};
